// src/pages/Suppliers.jsx
import React, { useEffect, useState } from "react";
import axios from "axios";
import Select from "react-select";
import DataTable from "react-data-table-component";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_BASE_URL } from "../../config";

const Suppliers = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [countries, setCountries] = useState([]);

  const [showForm, setShowForm] = useState(false);
  const [filterText, setFilterText] = useState("");

  const [editModal, setEditModal] = useState(false);
  const [viewModal, setViewModal] = useState(false);

  const emptyForm = {
    name: "",
    company_name: "",
    contact_number: "",
    email: "",
    website: "",
    phone_number: "",
    address_1: "",
    address_2: "",
    city: "",
    state: "",
    zipcode: "",
    gst_no: "",
    pan_no: "",
    country: "",
  };

  const [form, setForm] = useState(emptyForm);
  const [editForm, setEditForm] = useState({
    id: "",
    name: "",
    company_name: "",
    contact_number: "",
    email: "",
    website: "",
    phone_number: "",
    address_1: "",
    address_2: "",
    city: "",
    state: "",
    zipcode: "",
    gst_no: "",
    pan_no: "",
    country: "",
    status: "1",
  });

  const [viewData, setViewData] = useState(null);

  const authHeader = () => ({
    Authorization: `Bearer ${localStorage.getItem("authToken")}`,
  });

  /* =======================================================
   * LOADERS
   * ======================================================= */
  const loadSuppliers = async () => {
    try {
      const res = await axios.get(API_BASE_URL + "/suppliersList", {
        headers: authHeader(),
      });
      setSuppliers(res.data.data || []);
    } catch (err) {
      toast.error("Failed to load suppliers");
      console.error(err);
    }
  };

  const loadCountries = async () => {
    try {
      const res = await axios.get(API_BASE_URL + "/countries", {
        headers: authHeader(),
      });
      setCountries(res.data.data || []);
    } catch (err) {
      toast.error("Failed to load countries");
      console.error(err);
    }
  };

  useEffect(() => {
    loadSuppliers();
    loadCountries();
  }, []);

  /* =======================================================
   * ADD SUPPLIER
   * ======================================================= */
  const handleInput = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(API_BASE_URL + "/add_supplier", form, {
        headers: authHeader(),
      });

      toast.success("Supplier added successfully!");
      setForm(emptyForm);
      setShowForm(false);
      loadSuppliers();
    } catch (err) {
      if (err.response?.status === 422 && err.response.data.errors) {
        Object.values(err.response.data.errors).forEach((msg) =>
          toast.error(msg[0])
        );
      } else {
        toast.error("Error adding supplier");
      }
    }
  };

  /* =======================================================
   * DELETE SUPPLIER
   * ======================================================= */
  const deleteSupplier = async (id) => {
    if (!window.confirm("Delete this supplier?")) return;
    try {
      await axios.delete(API_BASE_URL + `/suppliers/${id}`, {
        headers: authHeader(),
      });
      toast.success("Supplier deleted!", { position: "bottom-right" });
      loadSuppliers();
    } catch (err) {
      toast.error("Error deleting supplier");
      console.error(err);
    }
  };

  /* =======================================================
   * OPEN VIEW MODAL
   * ======================================================= */
  const openViewModal = async (id) => {
    try {
      const res = await axios.get(API_BASE_URL + `/supplier_show/${id}`, {
        headers: authHeader(),
      });
      setViewData(res.data.data);
      setViewModal(true);
    } catch (err) {
      toast.error("Failed to load supplier details");
      console.error(err);
    }
  };

  /* =======================================================
   * OPEN EDIT MODAL
   * ======================================================= */
  const openEditModal = async (id) => {
    try {
      const res = await axios.get(API_BASE_URL + `/supplier_show/${id}`, {
        headers: authHeader(),
      });
      const d = res.data.data;

      setEditForm({
        id: d.supplier_id,
        name: d.supplier_name,
        company_name: d.company_name,
        contact_number: d.contact_number || "",
        phone_number: d.phone_number || "",
        email: d.supplier_email || "",
        website: d.website_url || "",
        address_1: d.address_1 || "",
        address_2: d.address_2 || "",
        city: d.city || "",
        state: d.state || "",
        zipcode: d.zipcode || "",
        gst_no: d.gst_no || "",
        pan_no: d.pan_no || "",
        country: d.country || "",
        status: String(d.is_active ?? "1"),
      });

      setEditModal(true);
    } catch (err) {
      toast.error("Failed to load supplier for edit");
      console.error(err);
    }
  };

  const handleEditInput = (e) => {
    setEditForm({ ...editForm, [e.target.name]: e.target.value });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(
        API_BASE_URL + `/update_supplier/${editForm.id}`,
        editForm,
        { headers: authHeader() }
      );

      toast.success("Supplier updated successfully!");
        
      setEditModal(false);
      loadSuppliers();
    } catch (err) {
      if (err.response?.status === 422 && err.response.data.errors) {
        Object.values(err.response.data.errors).forEach((msg) =>
          toast.error(msg[0])
        );
      } else {
        toast.error("Error updating supplier");
      }
      console.error(err);
    }
  };

  /* =======================================================
   * DATATABLE CONFIG
   * ======================================================= */
  const columns = [
    {
      name: "Action",
      width: "140px",
      cell: (row) => (
        <>
          {/* <button
            className="btn btn-outline-info btn-sm me-1"
            onClick={() => openViewModal(row.id)}
            title="View"
          >
            <i className="fa fa-eye" />
          </button> */}
           <button
        className="border-0 bg-transparent me-2"
        title="Edit"
        onClick={() => openEditModal(row.id)}
        style={{ cursor: "pointer" }}
      >
        <i className="fa fa-pencil text-warning" style={{ fontSize: "18px" }}></i>
          </button>
         
        </>
      ),
    },
    { name: "Supplier Name", selector: (row) => row.name, sortable: true },
    { name: "Company", selector: (row) => row.company_name, sortable: true },
    { name: "Email", selector: (row) => row.email, sortable: true },
    { name: "PAN No", selector: (row) => row.pan_no, sortable: true },
    { name: "GST No", selector: (row) => row.gst_no, sortable: true },
    { name: "City", selector: (row) => row.city, sortable: true },
    { name: "Country", selector: (row) => row.country_name, sortable: true },
  ];

  const filteredData = suppliers.filter((item) =>
    Object.values(item).some((val) =>
      String(val || "")
        .toLowerCase()
        .includes(filterText.toLowerCase())
    )
  );

  /* =======================================================
   * JSX
   * ======================================================= */
  return (
    <div className="container mt-4">
     <ToastContainer position="bottom-right" />

      {/* Top card with Add New button */}
      <div className="card mb-3">
        <div className="card-header d-flex justify-content-between align-items-center">
          <strong>Suppliers</strong>
          <button
            className="btn btn-outline-primary btn-sm"
            onClick={() => setShowForm((prev) => !prev)}
          >
            <i className="fa fa-plus" />{" "}
            {showForm ? "Close" : "Add New"}
          </button>
        </div>
      </div>

      {/* Collapsible Add Form */}
      {showForm && (
        <div className="card mb-3">
          <div className="card-header">
            <strong>Add New Supplier</strong>
          </div>
          <div className="card-body">
            <form onSubmit={handleSubmit}>
              <div className="row">
                {/* LEFT SIDE */}
                <div className="col-md-6">
                  <div className="mb-3">
                    <label>Supplier Name </label>
                    <input
                      type="text"
                      name="name"
                      className="form-control required-input"
                      value={form.name}
                      onChange={handleInput}
                    />
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label>Company </label>
                      <input
                        type="text"
                        name="company_name"
                        className="form-control required-input"
                        value={form.company_name}
                        onChange={handleInput}
                      />
                    </div>

                    <div className="col-md-6">
                      <label>Mobile Number</label>
                     <input
  type="text"
  name="contact_number"
  className="form-control require-input"
  value={form.contact_number}
  maxLength={10}
  onChange={(e) => {
    const value = e.target.value.replace(/\D/g, ""); // allow only digits
    handleInput({
      target: {
        name: "contact_number",
        value: value.slice(0, 10), // limit to 10 digits
      }
    });
  }}
/>

                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label>Email</label>
                      <input
                        type="email"
                        name="email"
                        className="form-control require-input"
                        value={form.email}
                        onChange={handleInput}
                      />
                    </div>

                    <div className="col-md-6">
                      <label>Website</label>
                      <input
                        type="text"
                        name="website"
                        className="form-control"
                        value={form.website}
                        onChange={handleInput}
                      />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label>Phone Number</label>
                   <input
  type="text"
  name="phone_number"
  className="form-control required-input"
  value={form.phone_number}
  maxLength={10}
  onChange={(e) => {
    const value = e.target.value.replace(/\D/g, ""); // keep only digits
    handleInput({
      target: {
        name: "phone_number",
        value: value.slice(0, 10), // limit to 10 digits
      }
    });
  }}
/>

                  </div>
                </div>

                {/* RIGHT SIDE */}
                <div className="col-md-6">
                  <label>Address</label>
                  <input
                    type="text"
                    name="address_1"
                    className="form-control mb-2 required-input"
                    placeholder="Address Line 1"
                    value={form.address_1}
                    onChange={handleInput}
                  />
                  <input
                    type="text"
                    name="address_2"
                    className="form-control mb-2"
                    placeholder="Address Line 2"
                    value={form.address_2}
                    onChange={handleInput}
                  />

                  <div className="row mb-3">
                    <div className="col-md-4">
                      <input
                        type="text"
                        name="city"
                        className="form-control required-input"
                        placeholder="City"
                        value={form.city}
                        onChange={handleInput}
                      />
                    </div>
                    <div className="col-md-4">
                      <input
                        type="text"
                        name="state"
                        className="form-control required-input"
                        placeholder="State"
                        value={form.state}
                        onChange={handleInput}
                      />
                    </div>
                    <div className="col-md-4">
                      <input
                        type="text"
                        name="zipcode"
                        className="form-control required-input"
                        placeholder="Zipcode"
                        value={form.zipcode}
                        onChange={handleInput}
                      />
                    </div>
                  </div>

                  <div className="row mb-3">
                    <div className="col-md-6">
                      <input
                        type="text"
                        name="gst_no"
                        className="form-control"
                        placeholder="GST No"
                        value={form.gst_no}
                        onChange={handleInput}
                      />
                    </div>
                    <div className="col-md-6">
                      <input
                        type="text"
                        name="pan_no"
                        className="form-control"
                        placeholder="PAN No"
                        value={form.pan_no}
                        onChange={handleInput}
                      />
                    </div>
                  </div>

                  <Select classNames={{
    control: () => "required-select required-tooltip"
  }}
                    options={countries.map((c) => ({
                      value: c.country_id,
                      label: c.country_name,
                    }))}
                    value={
                      countries
                        .map((c) => ({
                          value: c.country_id,
                          label: c.country_name,
                        }))
                        .find((opt) => opt.value === form.country) || null
                    }
                    onChange={(s) =>
                      setForm({ ...form, country: s ? s.value : "" })
                    }
                    placeholder="Select Country"
                    className="form-control p-0"
                  />
                </div>
              </div>

              <button type="submit" className="btn btn-primary mt-3">
                Save Supplier
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Supplier List card */}
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <strong>Supplier List</strong>
          <input
            type="text"
            className="form-control form-control-sm"
            style={{ width: "200px" }}
            placeholder="Search..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
        </div>

        <div className="card-body">
          <DataTable
            columns={columns}
            data={filteredData}
            pagination
            highlightOnHover
            striped
          />
        </div>
      </div>

      {/* ================= VIEW MODAL ================= */}
      {viewModal && viewData && (
        <div className="modal d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5>View Supplier</h5>
                <button
                  className="btn-close"
                  onClick={() => setViewModal(false)}
                />
              </div>
              <div className="modal-body">
                <table className="table table-striped table-bordered">
                  <tbody>
                    <tr>
                      <th>Supplier Name</th>
                      <td>{viewData.supplier_name}</td>
                    </tr>
                    <tr>
                      <th>Company Name</th>
                      <td>{viewData.company_name}</td>
                    </tr>
                    <tr>
                      <th>Contact Number</th>
                      <td>{viewData.contact_number}</td>
                    </tr>
                    <tr>
                      <th>Phone Number</th>
                      <td>{viewData.phone_number}</td>
                    </tr>
                    <tr>
                      <th>Email</th>
                      <td>{viewData.supplier_email}</td>
                    </tr>
                    <tr>
                      <th>Website</th>
                      <td>{viewData.website_url}</td>
                    </tr>
                    <tr>
                      <th>Address 1</th>
                      <td>{viewData.address_1}</td>
                    </tr>
                    {viewData.address_2 && (
                      <tr>
                        <th>Address 2</th>
                        <td>{viewData.address_2}</td>
                      </tr>
                    )}
                    <tr>
                      <th>City</th>
                      <td>{viewData.city}</td>
                    </tr>
                    <tr>
                      <th>State</th>
                      <td>{viewData.state}</td>
                    </tr>
                    <tr>
                      <th>Zipcode</th>
                      <td>{viewData.zipcode}</td>
                    </tr>
                    <tr>
                      <th>GST No</th>
                      <td>{viewData.gst_no}</td>
                    </tr>
                    <tr>
                      <th>PAN No</th>
                      <td>{viewData.pan_no}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => setViewModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ================= EDIT MODAL ================= */}
      {editModal && (
        <div className="modal d-block" style={{ background: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5>Edit Supplier</h5>
               <button
  type="button"
  onClick={() => setEditModal(false)}
  style={{
    border: "none",
    background: "transparent",
    fontSize: "22px",
    cursor: "pointer",
  }}
>
  ✕
</button>

              </div>
              <div className="modal-body">
                <form onSubmit={handleEditSubmit}>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="mb-3">
                        <label>Supplier Name</label>
                        <input
                          type="text"
                          name="name"
                          className="form-control"
                          value={editForm.name}
                          onChange={handleEditInput}
                        />
                      </div>

                      <div className="row mb-3">
                        <div className="col-md-6">
                          <label>Company Name</label>
                          <input
                            type="text"
                            name="company_name"
                            className="form-control"
                            value={editForm.company_name}
                            onChange={handleEditInput}
                          />
                        </div>
                        <div className="col-md-6">
                          <label>Contact Number</label>
                         <input
  type="text"
  name="contact_number"
  className="form-control"
  value={editForm.contact_number}
  maxLength={10}
  onChange={(e) => {
    const value = e.target.value.replace(/\D/g, ""); // keep only digits
    handleEditInput({
      target: {
        name: "contact_number",
        value: value.slice(0, 10), // limit to 10 digits
      }
    });
  }}
/>

                        </div>
                      </div>

                      <div className="row mb-3">
                        <div className="col-md-6">
                          <label>Email</label>
                          <input
                            type="email"
                            name="email"
                            className="form-control"
                            value={editForm.email}
                            onChange={handleEditInput}
                          />
                        </div>
                        <div className="col-md-6">
                          <label>Website</label>
                          <input
                            type="text"
                            name="website"
                            className="form-control"
                            value={editForm.website}
                            onChange={handleEditInput}
                          />
                        </div>
                      </div>

                      <div className="mb-3">
                        <label>Phone Number</label>
                        <input
  type="text"
  name="phone_number"
  className="form-control"
  value={editForm.phone_number}
  maxLength={10}
  onChange={(e) => {
    const value = e.target.value.replace(/\D/g, ""); // allow only digits
    handleEditInput({
      target: {
        name: "phone_number",
        value: value.slice(0, 10), // restrict to 10 digits
      }
    });
  }}
/>

                      </div>
                    </div>

                    <div className="col-md-6">
                      <label>Address</label>
                      <input
                        type="text"
                        name="address_1"
                        className="form-control mb-2"
                        placeholder="Address Line 1"
                        value={editForm.address_1}
                        onChange={handleEditInput}
                      />
                      <input
                        type="text"
                        name="address_2"
                        className="form-control mb-2"
                        placeholder="Address Line 2"
                        value={editForm.address_2}
                        onChange={handleEditInput}
                      />

                      <div className="row mb-3">
                        <div className="col-md-4">
                          <input
                            type="text"
                            name="city"
                            className="form-control"
                            placeholder="City"
                            value={editForm.city}
                            onChange={handleEditInput}
                          />
                        </div>
                        <div className="col-md-4">
                          <input
                            type="text"
                            name="state"
                            className="form-control"
                            placeholder="State"
                            value={editForm.state}
                            onChange={handleEditInput}
                          />
                        </div>
                        <div className="col-md-4">
                          <input
                            type="text"
                            name="zipcode"
                            className="form-control"
                            placeholder="Zipcode"
                            value={editForm.zipcode}
                            onChange={handleEditInput}
                          />
                        </div>
                      </div>

                      <div className="row mb-3">
                        <div className="col-md-6">
                          <input
                            type="text"
                            name="gst_no"
                            className="form-control"
                            placeholder="GST No"
                            value={editForm.gst_no}
                            onChange={handleEditInput}
                          />
                        </div>
                        <div className="col-md-6">
                          <input
                            type="text"
                            name="pan_no"
                            className="form-control"
                            placeholder="PAN No"
                            value={editForm.pan_no}
                            onChange={handleEditInput}
                          />
                        </div>
                      </div>

                      <div className="mb-3">
                        <Select
                          options={countries.map((c) => ({
                            value: c.country_id,
                            label: c.country_name,
                          }))}
                          value={
                            countries
                              .map((c) => ({
                                value: c.country_id,
                                label: c.country_name,
                              }))
                              .find((opt) => opt.value === editForm.country) ||
                            null
                          }
                          onChange={(s) =>
                            setEditForm({
                              ...editForm,
                              country: s ? s.value : "",
                            })
                          }
                          placeholder="Select Country"
                          className="form-control p-0"
                        />
                      </div>

                      <div className="mb-3">
                        <label>Status</label>
                        <select
                          name="status"
                          className="form-control"
                          value={editForm.status}
                          onChange={handleEditInput}
                        >
                          <option value="0">Inactive</option>
                          <option value="1">Active</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  <div className="modal-footer px-0">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setEditModal(false)}
                    >
                      Close
                    </button>
                    <button type="submit" className="btn btn-primary">
                      Update
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Suppliers;
